<template>
  <div @click="toggle" class="menu-item">
    <div class="cursor-pointer sbutton">
      <slot name="default" :isActive="isActive"></slot>
    </div>

    <VueFinalModal
      v-model="isActive"
      content-class="px-4 py-8 relative w-full h-full overflow-y-auto bg-black/80"
      :focus-trap="{}"
      esc-to-close
    >
      <div
        method="dialog"
        class="absolute transform left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/3 w-[calc(100%-32px)] max-w-[740px] py-4 lg:py-8 px-6 lg:px-12 flex flex-col gap-6 backdrop-blur-lg bg-neutral-50/80 dark:bg-neutral-800/80 text-neutral-800 dark:text-neutral-300 rounded-2xl shadow-md"
      >
        <div class="flex items-center justify-between gap-4">
          <span class="font-semibold text-sm md:text-xl">{{ $t('placeholder.contentSearch') }}</span>
          <UiButton
            type="ghost"
            icon="x"
            iconSize="24"
            class="px-4 h-10"
            :text="$t('button.close')"
            @click="() => cancel()"
          />
        </div>

        <div class="flex gap-2">
          <label :class="[
            'mb-6 grow flex items-center bg-neutral-200/50 dark:bg-neutral-800/70 rounded-full border transition-colors',
            isInputFocused 
              ? 'border-[--c-primary-500] shadow-[0_0_2px_0_var(--c-primary-500)] dark:border-[--c-primary-600] dark:shadow-[0_0_2px_0_var(--c-primary-600)]'
              : 'dark:border-neutral-400'
          ]">
            <UiIcon icon="search" :size="24" class="shrink-0 text-[#666] ml-2" />
            <input
              ref="searchInput"
              type="text"
              v-model="searchQuery"
              class="w-full border-0 bg-transparent p-3 text-base text-[#212121] dark:text-neutral-200 placeholder-neutral-500 focus:outline-none dark:placeholder-neutral-400"
              :placeholder="$t('placeholder.searchContent')"
              @focus="isInputFocused = true"
              @blur="isInputFocused = false"
              @keyup.enter="submit"
              data-testid="sidebar-search-input"
            />
            <UiButton
              type="primary"
              round
              icon="arrow-right"
              icon-size="24px"
              :disabled="!searchQuery.length"
              class="p-2 mr-1"
              @click="submit"
            />
          </label>
        </div>

        <div class="text-xs text-center" v-html="$t('tooltips.searchShortcut')"></div>
      </div>
    </VueFinalModal>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { VueFinalModal } from 'vue-final-modal'
import { useMagicKeys, whenever } from '@vueuse/core'

import UiButton from '@/components/ui/button.vue'
import UiIcon from '@/components/ui/icon.vue'

const router = useRouter()

const isActive = ref(false)
const searchQuery = ref('')
const searchInput = ref<HTMLInputElement | null>(null)
const isInputFocused = ref(false)

const toggle = () => {
  isActive.value = !isActive.value
}

const cancel = () => {
  isActive.value = false
  searchQuery.value = ''
}

whenever(isActive,
  () => {
    // Focus the search input when the modal opens
    setTimeout(() => {
      searchInput.value?.focus()
    }, 100)
  }
)

const { ctrl_k } = useMagicKeys({
  passive: false,
  onEventFired(e) {
    if (e.ctrlKey && e.key === 'k' && e.type === 'keydown')
      e.preventDefault()

    if (isActive.value && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) e.preventDefault()
  }
})

whenever(ctrl_k, () => {
  if (!isActive.value) {
    isActive.value = true
  }
})

const submit = () => {
  router.push('/search?query=' + encodeURIComponent(searchQuery.value))
  cancel()
}
</script>

<style scoped lang="scss">
.menu-item {
  .sbutton {
    @apply relative block rounded-md text-inherit no-underline transition-all hover:text-black dark:text-neutral-300 dark:hover:text-neutral-300;
  }

  &:not(.active) > .sbutton {
    @apply hover:bg-neutral-200/75 dark:hover:bg-neutral-700/30 hover:backdrop-blur-md hover:text-[--c-primary-800];
  }

  &.active > .sbutton {
    @apply bg-[--c-primary-500] font-semibold text-[--c-primary-at-800] hover:text-[--c-primary-at-900] dark:text-[--c-primary-at-500];
  }
}
</style>